import { useTeamMemberStore } from "@/stores/teamMemberStore";
import { useUserStore } from "@/stores/userStore";
import { useGetTeamMembersByClientId } from "@/services/teamService";
import { useEffect } from "react";

/**
 * Hook to sync team member data into the teamMemberStore.
 * - Only fetches when the logged-in user has role "User"
 * - Refreshes automatically when impersonation begins or exits
 */
export const useTeamMemberSync = () => {
  const { user,currentCompany, superAdminStore } = useUserStore();
  const { setTeamMembers, setLoading, setError, setClientAndCompany, clearTeamMembers } =
    useTeamMemberStore();

  // If superAdminStore exists, we're impersonating — use `user` (which is the impersonated user)
  const activeUser = user;
  const isUserRole = activeUser?.role === "User";
  const isImpersonating = !!superAdminStore;

  const clientId = isUserRole ? (activeUser?.clientId || "") : "";
  const companyId = isUserRole ? (currentCompany?.id || "") : "";

  useEffect(() => {
    if (isUserRole && clientId && companyId) {
      setClientAndCompany(clientId, companyId);
    } else {
      clearTeamMembers();
    }
  }, [isUserRole, clientId, companyId, isImpersonating, setClientAndCompany, clearTeamMembers]);

  const { data, isLoading, isError, error } = useGetTeamMembersByClientId(clientId, companyId);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  useEffect(() => {
    if (isError) {
      setError(error?.message || "Failed to fetch team members");
    }
  }, [isError, error, setError]);

  useEffect(() => {
    if (data?.data) {
      setTeamMembers(data.data);
    }
  }, [data, setTeamMembers]);

  return { teamMembers: data?.data || [], isLoading, isError };
};